import { polygonType } from '../../../../types/mvpMap';
import * as turf from '@turf/turf';

type LatLng = { lat: number; lng: number };

// Function to check if a point is inside the polygon
export const isPointInPolygon = (
  point: LatLng,
  polygon: polygonType[]
): boolean => {
  // Create a GeoJSON point for the given lat/lng
  const turfPoint = turf.point([point.lng, point.lat]);

  // Convert the polygon to GeoJSON format
  const geoJSONPolygon = turf.polygon([
    polygon.map((point) => [point.lng, point.lat]), // Ensure the coordinates are in [lng, lat] format
  ]);

  // Use Turf.js to check if the point is inside the polygon
  return turf.booleanPointInPolygon(
    turfPoint,
    geoJSONPolygon
  );
};

type Color = { R: number; G: number; B: number };

export const colorMapping = (
  indexName: string | undefined | null,
  value: number | undefined
): Color => {
  if ((!value && value != 0) || !indexName) {
    return { R: 0, G: 0, B: 0 };
  }

  // Helper function to convert hex color to RGB
  function hexToRgb(hex: string): Color {
    const bigint = parseInt(hex.substring(1), 16);
    return {
      R: (bigint >> 16) & 255,
      G: (bigint >> 8) & 255,
      B: bigint & 255,
    };
  }

  let colorHex: string = '';

  switch (indexName) {
    case 'NDVI':
    case 'EVI':
      if (value <= -1.0) {
        colorHex = '#800000'; // Open Soil
      } else if (value <= -0.2) {
        colorHex = '#950000'; // Open Soil
      } else if (value <= -0.1) {
        colorHex = '#aa0000'; // Open Soil
      } else if (value <= 0.0) {
        colorHex = '#b50000'; // Open Soil
      } else if (value <= 0.03) {
        colorHex = '#c00000'; // Open Soil
      } else if (value <= 0.05) {
        colorHex = '#d50000'; // Open Soil
      } else if (value <= 0.1) {
        colorHex = '#ea0000'; // Open Soil
      } else if (value <= 0.2) {
        colorHex = '#ff0000'; // Sparse Vegetation
      } else if (value <= 0.3) {
        colorHex = '#ffaa00'; // Low Vegetation
      } else if (value <= 0.4) {
        colorHex = '#ffff00'; // Low Vegetation
      } else if (value <= 0.5) {
        colorHex = '#eaff0c'; // Moderate Vegetation
      } else if (value <= 0.55) {
        colorHex = '#adff2f'; // Moderate Vegetation
      } else if (value <= 0.6) {
        colorHex = '#90dd2c'; // Moderate Vegetation
      } else if (value <= 0.65) {
        colorHex = '#74bb2a'; // Dense Vegetation
      } else if (value <= 0.7) {
        colorHex = '#579828'; // Dense Vegetation
      } else if (value <= 0.75) {
        colorHex = '#498726'; // Dense Vegetation
      } else if (value <= 0.8) {
        colorHex = '#3a7625'; // Dense Vegetation
      } else if (value <= 0.85) {
        colorHex = '#2c6524'; // Dense Vegetation
      } else if (value <= 0.9) {
        colorHex = '#1e5422'; // Very Dense Vegetation
      } else if (value <= 0.95) {
        colorHex = '#0f4321'; // Forest
      } else {
        colorHex = '#013220'; // Forest
      }
      break;

    case 'ARVI':
    case 'CIG':
    case 'CCCI':
    case 'EGS':
    case 'TRUE_GREEN_GAUGE':
    case 'PVI':
    case 'ATSAVI':
    case 'PPI':
    case 'CHI_VEGETABLES':
    case 'CHI_ORCHARDS':
      if (value <= -1.0) {
        colorHex = '#800000';
      } else if (value <= -0.2) {
        colorHex = '#950000';
      } else if (value <= -0.1) {
        colorHex = '#aa0000';
      } else if (value <= 0.0) {
        colorHex = '#b50000';
      } else if (value <= 0.03) {
        colorHex = '#c00000';
      } else if (value <= 0.05) {
        colorHex = '#d50000';
      } else if (value <= 0.1) {
        colorHex = '#ea0000';
      } else if (value <= 0.2) {
        colorHex = '#ff0000';
      } else if (value <= 0.3) {
        colorHex = '#ffaa00';
      } else if (value <= 0.4) {
        colorHex = '#ffff00';
      } else if (value <= 0.5) {
        colorHex = '#eaff0c';
      } else if (value <= 0.55) {
        colorHex = '#adff2f';
      } else if (value <= 0.6) {
        colorHex = '#90dd2c';
      } else if (value <= 0.65) {
        colorHex = '#74bb2a';
      } else if (value <= 0.7) {
        colorHex = '#579828';
      } else if (value <= 0.75) {
        colorHex = '#498726';
      } else if (value <= 0.8) {
        colorHex = '#3a7625';
      } else if (value <= 0.85) {
        colorHex = '#2c6524';
      } else if (value <= 0.9) {
        colorHex = '#1e5422';
      } else if (value <= 0.95) {
        colorHex = '#0f4321';
      } else if (value <= 1.0) {
        colorHex = '#013220';
      }
      break;

    case 'NDWI':
    case 'VWS':
    case 'LWCI':
      if (value <= -1.0) colorHex = '#f7fbff';
      else if (value <= -0.8947368421052629)
        colorHex = '#edf5fc';
      else if (value <= -0.789473684210527)
        colorHex = '#e3eef9';
      else if (value <= -0.6842105263157903)
        colorHex = '#d9e8f5';
      else if (value <= -0.5789473684210538)
        colorHex = '#cfe1f2';
      else if (value <= -0.4736842105263174)
        colorHex = '#c5dbef';
      else if (value <= -0.36842105263158076)
        colorHex = '#b5d4e9';
      else if (value <= -0.26315789473684426)
        colorHex = '#a5cde3';
      else if (value <= -0.15789473684210775)
        colorHex = '#92c3de';
      else if (value <= -0.05263157894737103)
        colorHex = '#7db8da';
      else if (value <= 0.05263157894737103)
        colorHex = '#69add5';
      else if (value <= 0.15789473684210753)
        colorHex = '#58a1cf';
      else if (value <= 0.26315789473684426)
        colorHex = '#4896c8';
      else if (value <= 0.36842105263158076)
        colorHex = '#3989c2';
      else if (value <= 0.47368421052631704)
        colorHex = '#2c7cbb';
      else if (value <= 0.578947368421054)
        colorHex = '#1f6eb3';
      else if (value <= 0.6842105263157903)
        colorHex = '#1460a8';
      else if (value <= 0.7894736842105268)
        colorHex = '#09529d';
      else if (value <= 0.8947368421052633)
        colorHex = '#084185';
      else colorHex = '#08306b';
      break;

    case 'VNI':
      if (value <= -1.0) colorHex = '#ffffb2';
      else if (value <= -0.8947368421052629)
        colorHex = '#fff4a0';
      else if (value <= -0.789473684210527)
        colorHex = '#ffea8e';
      else if (value <= -0.6842105263157903)
        colorHex = '#fedf7c';
      else if (value <= -0.5789473684210538)
        colorHex = '#fed46a';
      else if (value <= -0.4736842105263174)
        colorHex = '#fec95a';
      else if (value <= -0.36842105263158076)
        colorHex = '#febb54';
      else if (value <= -0.26315789473684426)
        colorHex = '#feae4d';
      else if (value <= -0.15789473684210775)
        colorHex = '#fda146';
      else if (value <= -0.05263157894737103)
        colorHex = '#fd943f';
      else if (value <= 0.05263157894737103)
        colorHex = '#fc8439';
      else if (value <= 0.15789473684210753)
        colorHex = '#f97333';
      else if (value <= 0.26315789473684426)
        colorHex = '#f6622d';
      else if (value <= 0.36842105263158076)
        colorHex = '#f35127';
      else if (value <= 0.47368421052631704)
        colorHex = '#f13f21';
      else if (value <= 0.578947368421054)
        colorHex = '#e83221';
      else if (value <= 0.6842105263157903)
        colorHex = '#dd2522';
      else if (value <= 0.7894736842105268)
        colorHex = '#d21923';
      else if (value <= 0.8947368421052633)
        colorHex = '#c80c25';
      else colorHex = '#bd0026';
      break;

    case 'BSI':
      if (value <= 0.0) colorHex = '#ffffb2';
      else if (value <= 0.10526315789473707)
        colorHex = '#fff4a0';
      else if (value <= 0.21052631578947306)
        colorHex = '#ffea8e';
      else if (value <= 0.3157894736842097)
        colorHex = '#fedf7c';
      else if (value <= 0.4210526315789462)
        colorHex = '#fed46a';
      else if (value <= 0.5263157894736826)
        colorHex = '#fec95a';
      else if (value <= 0.6315789473684192)
        colorHex = '#febb54';
      else if (value <= 0.7368421052631557)
        colorHex = '#feae4d';
      else if (value <= 0.8421052631578922)
        colorHex = '#fda146';
      else if (value <= 0.947368421052629)
        colorHex = '#fd943f';
      else if (value <= 1.052631578947371)
        colorHex = '#fc8439';
      else if (value <= 1.1578947368421075)
        colorHex = '#f97333';
      else if (value <= 1.2631578947368443)
        colorHex = '#f6622d';
      else if (value <= 1.3684210526315808)
        colorHex = '#f35127';
      else if (value <= 1.473684210526317)
        colorHex = '#f13f21';
      else if (value <= 1.578947368421054)
        colorHex = '#e83221';
      else if (value <= 1.6842105263157903)
        colorHex = '#dd2522';
      else if (value <= 1.7894736842105268)
        colorHex = '#d21923';
      else if (value <= 1.8947368421052633)
        colorHex = '#c80c25';
      else colorHex = '#bd0026';
      break;

    case 'LAI':
    case 'CHI':
      if (value <= 0.0) colorHex = '#d7191c';
      else if (value <= 0.2631578947368427)
        colorHex = '#df382b';
      else if (value <= 0.5263157894736826)
        colorHex = '#e75839';
      else if (value <= 0.7894736842105242)
        colorHex = '#ef7748';
      else if (value <= 1.0526315789473655)
        colorHex = '#f79656';
      else if (value <= 1.3157894736842066)
        colorHex = '#fdb266';
      else if (value <= 1.5789473684210482)
        colorHex = '#fec37a';
      else if (value <= 1.8421052631578894)
        colorHex = '#fed48e';
      else if (value <= 2.1052631578947305)
        colorHex = '#fee5a2';
      else if (value <= 2.3684210526315725)
        colorHex = '#fff6b6';
      else if (value <= 2.6315789473684275)
        colorHex = '#f6fbb7';
      else if (value <= 2.894736842105269)
        colorHex = '#e3f3a5';
      else if (value <= 3.1578947368421106)
        colorHex = '#d0eb93';
      else if (value <= 3.421052631578952)
        colorHex = '#bde381';
      else if (value <= 3.6842105263157925)
        colorHex = '#abdb6f';
      else if (value <= 3.947368421052635)
        colorHex = '#90ce64';
      else if (value <= 4.210526315789476)
        colorHex = '#72c05b';
      else if (value <= 4.473684210526317)
        colorHex = '#55b252';
      else if (value <= 4.736842105263158)
        colorHex = '#37a44a';
      else colorHex = '#1a9641';
      break;

    case 'CSI':
      if (value <= 0.0) {
        colorHex = '#1a9641';
      } else if (value <= 0.052631578947368536) {
        colorHex = '#37a44a';
      } else if (value <= 0.10526315789473653) {
        colorHex = '#55b252';
      } else if (value <= 0.15789473684210484) {
        colorHex = '#72c05b';
      } else if (value <= 0.2105263157894731) {
        colorHex = '#90ce64';
      } else if (value <= 0.2631578947368413) {
        colorHex = '#abdb6f';
      } else if (value <= 0.3157894736842096) {
        colorHex = '#bde381';
      } else if (value <= 0.36842105263157787) {
        colorHex = '#d0eb93';
      } else if (value <= 0.4210526315789461) {
        colorHex = '#e3f3a5';
      } else if (value <= 0.4736842105263145) {
        colorHex = '#f6fbb7';
      } else if (value <= 0.5263157894736855) {
        colorHex = '#fff6b6';
      } else if (value <= 0.5789473684210538) {
        colorHex = '#fee5a2';
      } else if (value <= 0.6315789473684221) {
        colorHex = '#fed48e';
      } else if (value <= 0.6842105263157904) {
        colorHex = '#fec37a';
      } else if (value <= 0.7368421052631585) {
        colorHex = '#fdb266';
      } else if (value <= 0.789473684210527) {
        colorHex = '#f79656';
      } else if (value <= 0.8421052631578951) {
        colorHex = '#ef7748';
      } else if (value <= 0.8947368421052634) {
        colorHex = '#e75839';
      } else if (value <= 0.9473684210526316) {
        colorHex = '#df382b';
      } else {
        colorHex = '#d7191c';
      }
      break;

    case 'MIX_LAI':
      if (value <= 0.0) {
        colorHex = '#d7191c';
      } else if (value <= 0.31578947368421123) {
        colorHex = '#df382b';
      } else if (value <= 0.6315789473684192) {
        colorHex = '#e75839';
      } else if (value <= 0.947368421052629) {
        colorHex = '#ef7748';
      } else if (value <= 1.2631578947368385) {
        colorHex = '#f79656';
      } else if (value <= 1.5789473684210478) {
        colorHex = '#fdb266';
      } else if (value <= 1.8947368421052577) {
        colorHex = '#fec37a';
      } else if (value <= 2.2105263157894672) {
        colorHex = '#fed48e';
      } else if (value <= 2.526315789473677) {
        colorHex = '#fee5a2';
      } else if (value <= 2.8421052631578867) {
        colorHex = '#fff6b6';
      } else if (value <= 3.1578947368421133) {
        colorHex = '#e3f3a5';
      } else if (value <= 3.4736842105263226) {
        colorHex = '#b1dc8c';
      } else if (value <= 3.7894736842105328) {
        colorHex = '#d0eb93';
      } else if (value <= 4.1052631578947425) {
        colorHex = '#bde381';
      } else if (value <= 4.421052631578951) {
        colorHex = '#abdb6f';
      } else if (value <= 4.736842105263162) {
        colorHex = '#90ce64';
      } else if (value <= 5.052631578947371) {
        colorHex = '#72c05b';
      } else if (value <= 5.3684210526315805) {
        colorHex = '#55b252';
      } else if (value <= 5.684210526315789) {
        colorHex = '#37a44a';
      } else if (value <= 6.0) {
        colorHex = '#1a9641';
      } else colorHex = '#1a9641';
      break;

    case 'CRI700':
    case 'ARI':
      if (value <= -1.0) {
        colorHex = '#d7191c';
      } else if (value <= -0.15789473684210342) {
        colorHex = '#df382b';
      } else if (value <= 0.6842105263157845) {
        colorHex = '#e75839';
      } else if (value <= 1.5263157894736774) {
        colorHex = '#ef7748';
      } else if (value <= 2.3684210526315694) {
        colorHex = '#f79656';
      } else if (value <= 3.210526315789461) {
        colorHex = '#fdb266';
      } else if (value <= 4.052631578947354) {
        colorHex = '#fec37a';
      } else if (value <= 4.894736842105246) {
        colorHex = '#fed48e';
      } else if (value <= 5.736842105263138) {
        colorHex = '#fee5a2';
      } else if (value <= 6.578947368421032) {
        colorHex = '#fff6b6';
      } else if (value <= 7.421052631578968) {
        colorHex = '#e3f3a5';
      } else if (value <= 8.26315789473686) {
        colorHex = '#b1dc8c';
      } else if (value <= 9.105263157894754) {
        colorHex = '#d0eb93';
      } else if (value <= 9.947368421052646) {
        colorHex = '#bde381';
      } else if (value <= 10.789473684210536) {
        colorHex = '#abdb6f';
      } else if (value <= 11.631578947368432) {
        colorHex = '#90ce64';
      } else if (value <= 12.473684210526322) {
        colorHex = '#72c05b';
      } else if (value <= 13.315789473684214) {
        colorHex = '#55b252';
      } else if (value <= 14.157894736842106) {
        colorHex = '#37a44a';
      } else if (value <= 15.0) {
        colorHex = '#1a9641';
      } else colorHex = '#1a9641';
      break;

    case 'SBI':
      if (value <= 0.0) colorHex = '#fafafa';
      else if (value <= 0.052631578947368536)
        colorHex = '#ededed';
      else if (value <= 0.10526315789473653)
        colorHex = '#e0e0e0';
      else if (value <= 0.15789473684210484)
        colorHex = '#d3d3d3';
      else if (value <= 0.2105263157894731)
        colorHex = '#c6c6c6';
      else if (value <= 0.2631578947368413)
        colorHex = '#bababa';
      else if (value <= 0.3157894736842096)
        colorHex = '#adadad';
      else if (value <= 0.36842105263157787)
        colorHex = '#a0a0a0';
      else if (value <= 0.4210526315789461)
        colorHex = '#939393';
      else if (value <= 0.4736842105263145)
        colorHex = '#868686';
      else if (value <= 0.5263157894736855)
        colorHex = '#797979';
      else if (value <= 0.5789473684210538)
        colorHex = '#6c6c6c';
      else if (value <= 0.6315789473684221)
        colorHex = '#5f5f5f';
      else if (value <= 0.6842105263157904)
        colorHex = '#525252';
      else if (value <= 0.7368421052631585)
        colorHex = '#454545';
      else if (value <= 0.789473684210527)
        colorHex = '#393939';
      else if (value <= 0.8421052631578951)
        colorHex = '#2c2c2c';
      else if (value <= 0.8947368421052634)
        colorHex = '#1f1f1f';
      else if (value <= 0.9473684210526316)
        colorHex = '#121212';
      else colorHex = '#050505';
      break;

    case 'LLCI':
      if (value <= -1.0) colorHex = '#fafafa';
      else if (value <= -0.8947368421052629)
        colorHex = '#ededed';
      else if (value <= -0.789473684210527)
        colorHex = '#e0e0e0';
      else if (value <= -0.6842105263157903)
        colorHex = '#d3d3d3';
      else if (value <= -0.5789473684210538)
        colorHex = '#c6c6c6';
      else if (value <= -0.4736842105263174)
        colorHex = '#bababa';
      else if (value <= -0.36842105263158076)
        colorHex = '#adadad';
      else if (value <= -0.26315789473684426)
        colorHex = '#a0a0a0';
      else if (value <= -0.15789473684210775)
        colorHex = '#939393';
      else if (value <= -0.05263157894737103)
        colorHex = '#868686';
      else if (value <= 0.05263157894737103)
        colorHex = '#797979';
      else if (value <= 0.15789473684210753)
        colorHex = '#6c6c6c';
      else if (value <= 0.26315789473684426)
        colorHex = '#5f5f5f';
      else if (value <= 0.36842105263158076)
        colorHex = '#525252';
      else if (value <= 0.47368421052631704)
        colorHex = '#454545';
      else if (value <= 0.578947368421054)
        colorHex = '#393939';
      else if (value <= 0.6842105263157903)
        colorHex = '#2c2c2c';
      else if (value <= 0.7894736842105268)
        colorHex = '#1f1f1f';
      else if (value <= 0.8947368421052633)
        colorHex = '#121212';
      else colorHex = '#050505';
      break;

    default:
      // No matching index, return a default white color
      colorHex = '#ffffff';
  }

  return hexToRgb(colorHex);
};
